import { useRef, useState } from "react";

import { Placement } from "@floating-ui/react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";
import { AnimatePresence, m } from "framer-motion";
import { debounce } from "lodash";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import {
  DateRangePicker,
  DateRangePickerProps,
  DateRangePickerResetable,
} from "src/base-components/DateRangePicker";
import { Icon } from "src/base-components/Icon";
import { SearchBoxProps as SearchBoxPropsBase } from "src/base-components/SearchBox";
import { Tooltip } from "src/design-system/Tooltip";

type ButtonProps = {
  as?: "button" | "div";
  icon: IconProp;
  tooltip: string;
  disabled?: boolean;
  dataLoc?: string;
  spin?: boolean;
  variant?: "default" | "active";
  onClick: () => void;
  tooltipPlacement?: Placement;
};

export type ButtonType = React.FC<ButtonProps>;

export const SubHeaderButton: ButtonType = ({
  disabled,
  icon,
  tooltip,
  dataLoc,
  spin,
  variant = "default",
  as: Tag = "button",
  onClick,
  tooltipPlacement,
}) => {
  const variantColor =
    variant === "active"
      ? "text-indigo-600 hover:text-indigo-700"
      : "text-gray-500 hover:text-gray-700";

  return (
    <Tooltip
      delay={350}
      placement={tooltipPlacement ?? "bottom"}
      title={tooltip}
      asChild
    >
      <Tag
        className="border-gray-200 border-l p-3.5 focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:ring-inset disabled:cursor-not-allowed disabled:bg-gray-50"
        data-loc={dataLoc}
        disabled={disabled}
        type="button"
        onClick={onClick}
      >
        <Icon
          color={twJoin(disabled ? "text-gray-300" : variantColor)}
          icon={icon}
          size="xs"
          spin={spin}
        />
      </Tag>
    </Tooltip>
  );
};

type SearchBoxProps = Omit<SearchBoxPropsBase, "value"> & { tooltip?: string };
export type SearchBoxType = React.FC<SearchBoxProps>;

export const SubHeaderSearchBox: SearchBoxType = ({
  dataLoc,
  placeholder,
  defaultValue,
  tooltip,
  onChange,
}) => {
  const [hasFocus, setFocus] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isOpen =
    hasFocus || Boolean(defaultValue) || Boolean(inputRef.current?.value);
  const debouncedOnChange = debounce(onChange, 300);

  return (
    <Tooltip disabled={!tooltip} placement="bottom" title={tooltip} asChild>
      <div
        className={twJoin(
          "flex h-full cursor-pointer items-center overflow-hidden border-gray-200 border-l px-3.5 py-1",
          hasFocus && "ring-2 ring-indigo-500/25 ring-inset",
        )}
        data-loc={`${dataLoc}-button`}
        onClick={() => {
          setFocus(true);
          inputRef.current?.focus();
        }}
      >
        <Icon color="text-gray-500" icon={faMagnifyingGlass} size="xs" />
        <AnimatePresence>
          <m.div
            animate={isOpen ? "open" : "closed"}
            className="h-full"
            initial={isOpen ? "open" : "closed"}
            transition={{ type: "tween", ease: "easeOut" }}
            variants={{
              open: { width: "14rem" },
              closed: { width: "0" },
            }}
          >
            <input
              ref={inputRef}
              className="h-full w-full bg-white pl-2 font-inter-normal-12px placeholder:text-gray-500 focus:outline-none"
              data-loc={dataLoc}
              defaultValue={defaultValue}
              placeholder={placeholder}
              onBlur={() => setFocus(false)}
              onChange={(event) => debouncedOnChange(event.target.value)}
              onFocus={() => setFocus(true)}
            />
          </m.div>
        </AnimatePresence>
      </div>
    </Tooltip>
  );
};

type DatePickerProps = Pick<
  DateRangePickerProps,
  | "onChange"
  | "value"
  | "placeholder"
  | "rangeLimitInDays"
  | "disabled"
  | "usePredefinedDates"
  | "predefinedDateOptions"
  | "suffixIcon"
> &
  DateRangePickerResetable;

export type DatePickerType = React.FC<DatePickerProps>;

export const SubHeaderDatePicker: DatePickerType = ({
  onChange,
  placeholder,
  value,
  disabled,
  usePredefinedDates,
  predefinedDateOptions,
  rangeLimitInDays,
  suffixIcon,
  ...resetableProps
}) => {
  return (
    <div
      className={twJoin(
        "flex h-full min-w-[15.5rem] items-center border-gray-200 border-l px-1.5",
        "[&:has(span[data-open])]:ring-2 [&:has(span[data-open])]:ring-indigo-500/25 [&:has(span[data-open])]:ring-inset",
      )}
    >
      <DateRangePicker
        disabled={disabled}
        placeholder={placeholder}
        predefinedDateOptions={predefinedDateOptions}
        rangeLimitInDays={rangeLimitInDays}
        suffixIcon={suffixIcon}
        usePredefinedDates={usePredefinedDates}
        value={value}
        borderless
        onChange={onChange}
        {...resetableProps}
      />
    </div>
  );
};

export const SubHeaderTabs: React.FC<{
  children: React.ReactNode;
  dataLoc?: string;
}> = ({ children, dataLoc }) => (
  <nav
    className="scrollbar-hide flex h-full gap-x-4 overflow-x-auto bg-white"
    data-loc={dataLoc}
  >
    {children}
  </nav>
);

type TabProps = {
  to: string;
  children: React.ReactNode;
  icon?: IconProp;
  end?: boolean;
  rightSlot?: React.ReactNode;
  dataLoc?: string;
};

export const SubHeaderTab: React.FC<TabProps> = ({
  to,
  children,
  icon,
  end,
  rightSlot,
  dataLoc,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const pathWithoutSearchParams = to.split("?")[0];
  const locationPathnameWithoutSearchParams = location.pathname.split("?")[0];

  const isActive = Boolean(
    matchPath(
      {
        path: pathWithoutSearchParams,
        end: end ?? false,
      },
      locationPathnameWithoutSearchParams,
    ),
  );

  const handleClick = () => {
    navigate(to);
  };

  return (
    <div
      className="relative inline-flex h-full items-center"
      data-active={isActive}
      data-loc={dataLoc}
    >
      <button
        className="flex h-full items-center px-1 font-inter-semibold-13px text-gray-800"
        data-active={isActive}
        type="button"
        onClick={handleClick}
      >
        <div className="flex items-center gap-x-1.5">
          {icon && <Icon color="text-gray-500" icon={icon} size="xs" />}
          <span className="whitespace-nowrap">{children}</span>
        </div>
      </button>
      {rightSlot && (
        <div className="ml-0.5 flex h-full items-center">{rightSlot}</div>
      )}
      {isActive && (
        <div className="absolute right-0 bottom-0 left-0 h-[3px] rounded-full bg-indigo-600" />
      )}
    </div>
  );
};

type Props = {
  leftSlot?: React.ReactNode;
  children?: React.ReactNode;
};

export const SubHeader: React.FC<Props> = ({ leftSlot, children }) => {
  return (
    <div className="flex h-12 shrink-0 items-center justify-between gap-x-2 overflow-hidden border-gray-200 border-b border-l bg-white pl-4">
      {leftSlot}
      {children && (
        <div className="flex h-full items-center self-end overflow-clip">
          {children}
        </div>
      )}
    </div>
  );
};
